import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton } from 'qampo-ui-library';
import { IconClock, IconDragIndicator, IconTrash } from '../../UI';
import { DependencyList } from './DependencyList';
import { InstanceBadge } from './InstanceBadge';
import { DAY_SHORT_LABEL_KEYS } from './constants';
import { fillFromColor, type NodeProps } from './nodeShared';
import styles from '../InterventionGraphEditor.module.css';

export const FreeNode = ({
  wi,
  strokeColor,
  fillColor,
  isOverlay = false,
  allWeekInterventions,
  getWiName,
  getWiLabel,
  onRemove,
  onRemoveDep,
  onAddDep,
  onEditAllowedDays
}: NodeProps) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const daysBtnRef = useRef<HTMLButtonElement>(null);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: wi.id
  });

  const interventionName = wi.intervention.name;
  const ivStroke = wi.intervention.color ?? strokeColor;
  const ivFill = wi.intervention.color ? fillFromColor(wi.intervention.color) : fillColor;

  const nodeStyle = {
    ...(isOverlay
      ? {}
      : {
          transform: CSS.Transform.toString(transform),
          transition,
          opacity: isDragging ? 0.25 : 1
        }),
    backgroundColor: ivFill,
    border: `2px dashed ${ivStroke}`
  };

  return (
    <div
      ref={isOverlay ? undefined : setNodeRef}
      style={nodeStyle}
      className={`${styles.freeNode} ${isOverlay ? styles.nodeOverlay : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...(isOverlay ? {} : attributes)}
    >
      <div className={styles.nodeHeader}>
        <div
          className={`${styles.dragHandle} ${hovered ? styles.dragHandleVisible : ''}`}
          {...(isOverlay ? {} : listeners)}
          title={t('config-drag-chain')}
          aria-label={t('config-drag-chain')}
        >
          <IconDragIndicator width={14} height={14} />
        </div>
        <InstanceBadge label={getWiLabel(wi.id)} backgroundColor={ivStroke} />
        <span className={styles.nodeName}>{interventionName}</span>
        {!isOverlay && (
          <span
            onClick={(e) => e.stopPropagation()}
            title={t('config-remove-btn')}
            aria-label={t('config-remove-btn')}
          >
            <IconButton
              icon={<IconTrash />}
              onClick={onRemove}
              size="small"
              classes={styles.deleteIconBtn}
            />
          </span>
        )}
      </div>
      <DependencyList
        wi={wi}
        allWeekInterventions={allWeekInterventions}
        getWiName={getWiName}
        getWiLabel={getWiLabel}
        isOverlay={isOverlay}
        strokeColor={ivStroke}
        onRemoveDep={onRemoveDep}
        onAddDep={onAddDep}
      />
      {wi.allowedDays && wi.allowedDays.length > 0 && (
        <div className={styles.dayIndicator}>
          {wi.allowedDays.map((d) => (
            <span key={d.day} className={styles.dayChip} style={{ borderColor: ivStroke }}>
              {t(DAY_SHORT_LABEL_KEYS[d.day])}
            </span>
          ))}
        </div>
      )}
      {!isOverlay && (
        <div className={styles.nodeActions}>
          <button
            ref={daysBtnRef}
            className={`${styles.daysBtn} ${wi.allowedDays !== undefined ? styles.daysBtnActive : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (daysBtnRef.current) {
                onEditAllowedDays(daysBtnRef.current.getBoundingClientRect());
              }
            }}
            title={t('config-edit-day-restrictions')}
          >
            <IconClock width={14} height={14} /> {t('config-days-btn')}
          </button>
        </div>
      )}
    </div>
  );
};
